import { DEFAULT_VESSEL_BEAM, DEFAULT_VESSEL_LOA } from 'data/static';
import { OffsetType, PositionType, VesselCategoryType, VesselType } from 'types';
import { DateTimeFormat } from './dateTimeFormat';


// get vessel center point of drawing
export function vesselCenter(
    sourcPosition: PositionType,
    vessel: VesselType,
    vesselOffset: OffsetType | undefined,
    center = true
): any {
    if (center || !vesselOffset) {
        return sourcPosition;
    }

    const LOA = vessel.loa_m || DEFAULT_VESSEL_LOA;
    const BEAM = vessel.beam_m || DEFAULT_VESSEL_BEAM;

    return {
        ...sourcPosition,
        east_utm: sourcPosition.east_utm + LOA / 2 - vesselOffset.stern_m,
        north_utm: sourcPosition.north_utm + BEAM / 2 - vesselOffset.stbd_m,
        heading_deg: sourcPosition.heading_deg,
    };
}

export function getPixelsPerMeter(zoom: number, latitude: number, tileSize = 256): number {
    const earthCircumference = 2 * Math.PI * 6378137;
    const latitudeRadians = (latitude * Math.PI) / 180;

    return (tileSize * Math.pow(2, zoom)) / (earthCircumference * Math.cos(latitudeRadians));
}

export function getVesselDisplayType(
    sourcPosition: PositionType,
    vessel: VesselType,
    zoom: number,
    latitude: number,
    minOutlineSizePx = 10
): 'outline' | 'arrow' | 'circle' {
    if (sourcPosition.heading_deg === 'N/A' || sourcPosition.heading_deg == null) {
        return 'circle';
    }

    const pixelsPerMeter = getPixelsPerMeter(zoom, latitude);
    const LOA = vessel.loa_m || DEFAULT_VESSEL_LOA;
    const vesselSizePx = LOA * pixelsPerMeter;

    if (vesselSizePx >= minOutlineSizePx) {
        return 'outline';
    }

    return 'arrow';
}

export function getVesselDisplayFeature(
    sourcPosition: PositionType,
    vessel: VesselType,
    vesselOffset: OffsetType | undefined,
    vesselCategory: VesselCategoryType,
    vesselCategories: VesselCategoryType[],
    portId: number,
    zoom: number,
    latitude: number,
    center = true
) {
    // Calculate vessel center
    const vesselCenterData = vesselCenter(sourcPosition, vessel, vesselOffset, center);

    const displayType = getVesselDisplayType(vesselCenterData, vessel, zoom, latitude);

    switch (displayType) {
        case 'outline':
            return {
                ...vesselOutline(vesselCenterData, sourcPosition, vessel, vesselCategory, vesselCategories, portId),
                displayType,
            };
        case 'arrow':
            return {
                ...vesselDirectionArrow(
                    vesselCenterData,
                    vesselCategory,
                    vesselCategories,
                    portId,
                    getPixelsPerMeter(zoom, latitude),
                    8
                ),
                displayType,
            };

        case 'circle':
            return {
                ...vesselCircle(vesselCenterData, vessel, vesselCategory, getPixelsPerMeter(zoom, latitude), 5),
                displayType,
            };
    }
}

export function vesselCircle(
    sourcPosition: PositionType,
    vessel: VesselType,
    vesselCategory: VesselCategoryType,
    pixelsPerMeter: number,
    targetSizePx = 24,
    segments = 24
) {
    const LOA = vessel.loa_m || DEFAULT_VESSEL_LOA;

    // Never smaller than the vessel LOA on screen,
    // while maintaining a minimum size when zoomed out.
    const diameterPx = Math.max(targetSizePx, LOA * pixelsPerMeter);

    const radius = diameterPx / (2 * pixelsPerMeter);

    const coordinates: number[][] = [];

    for (let i = 0; i <= segments; i++) {
        const angle = (i / segments) * Math.PI * 2;

        coordinates.push([
            sourcPosition.east_utm + radius * Math.cos(angle),
            sourcPosition.north_utm + radius * Math.sin(angle),
        ]);
    }

    return {
        status: false,
        color: vesselCategory.color,
        outline: coordinates,
    };
}

export function vesselDirectionArrow(
    sourcPosition: PositionType,
    vesselCategory: VesselCategoryType,
    vesselCategories: VesselCategoryType[],
    portId: number,
    pixelsPerMeter: number,
    targetSizePx = 5
) {
    const length = targetSizePx / pixelsPerMeter;
    const width = length * 0.7;

    const arrowShape = [
        [0, 0.5],
        [0.5, -0.5],
        [0, -0.25],
        [-0.5, -0.5],
        [0, 0.5],
    ];

    const coordinates: number[][] = arrowShape.map((point) => [
        point[0] * width + sourcPosition.east_utm,
        point[1] * length + sourcPosition.north_utm,
    ]);

    const customVesselCategory = vesselCategories.find((x) => x.port_id === portId && x.name === vesselCategory.name);

    return {
        status: false,
        color: customVesselCategory ? customVesselCategory.color : vesselCategory.color,
        //@ts-ignore
        outline: rotateUTMPolygonToHeading(coordinates, sourcPosition.heading_deg, [
            sourcPosition.east_utm,
            sourcPosition.north_utm,
        ]),
    };
}

export function vesselOutline(
    sourcPosition: PositionType,
    originalPosition: PositionType,
    vessel: VesselType,
    vesselCategory: VesselCategoryType,
    vesselCategories: VesselCategoryType[],
    portId: number
) {
    const LOA = vessel.loa_m || DEFAULT_VESSEL_LOA;
    const BEAM = vessel.beam_m || DEFAULT_VESSEL_BEAM;

    let vesselShape = [
        [-0.5, 0],
        [-0.5, 0.2258],
        [-0.4243, 0.3859],
        [-0.3673, 0.4668],
        [-0.3157, 0.5],
        [0.3291, 0.5],
        [0.3626, 0.4734],
        [0.3991, 0.4133],
        [0.4587, 0.2508],
        [0.4909, 0.1002],
        [0.5, 0],
        [0.4909, -0.1002],
        [0.4587, -0.2508],
        [0.3991, -0.4133],
        [0.3626, -0.4734],
        [0.3291, -0.5],
        [-0.3157, -0.5],
        [-0.3673, -0.4668],
        [-0.4243, -0.3859],
        [-0.5, -0.2258],
        [-0.5, 0],
    ];

    const customVesselCategory = vesselCategories.find((x) => x.port_id === portId && x.name === vesselCategory.name);

    if (customVesselCategory !== undefined) {
        vesselShape = customVesselCategory.vessel_shape;
    } else if (Array.isArray(vesselCategory.vessel_shape)) {
        vesselShape = vesselCategory.vessel_shape;
    }

    const coordinates: number[][] = vesselShape.map((point) => [
        point[0] * LOA + sourcPosition.east_utm,
        point[1] * BEAM + sourcPosition.north_utm,
    ]);

    return {
        status: false,
        color: customVesselCategory ? customVesselCategory.color : vesselCategory.color,
        //@ts-ignore
        outline: rotateUTMPolygonToHeading(coordinates, sourcPosition.heading_deg - 90, [
            originalPosition.east_utm,
            originalPosition.north_utm,
        ]),
    };
}

export function vesselOutline_(
    sourcPosition: PositionType,
    vessel: VesselType,
    vesselOffset: OffsetType | undefined,
    vesselCategory: VesselCategoryType,
    vesselCategories: VesselCategoryType[],
    portId: number,
    center = true
) {
    const LOA = vessel.loa_m && vessel.loa_m ? vessel.loa_m : DEFAULT_VESSEL_LOA;
    const BEAM = vessel.beam_m ? vessel.beam_m : DEFAULT_VESSEL_BEAM;

    if (sourcPosition.heading_deg == 'N/A' || sourcPosition.heading_deg == null || !vesselOffset) {
        return {
            status: true,
            color: vesselCategory.color,
            outline: createUnknownHeadingVessel(sourcPosition,  LOA / 2  ),
        };
    }

    let vesselShape = [
        [-0.5, 0],
        [-0.5, 0.2258],
        [-0.4243, 0.3859],
        [-0.3673, 0.4668],
        [-0.3157, 0.5],
        [0.3291, 0.5],
        [0.3626, 0.4734],
        [0.3991, 0.4133],
        [0.4587, 0.2508],
        [0.4909, 0.1002],
        [0.5, 0],
        [0.4909, -0.1002],
        [0.4587, -0.2508],
        [0.3991, -0.4133],
        [0.3626, -0.4734],
        [0.3291, -0.5],
        [-0.3157, -0.5],
        [-0.3673, -0.4668],
        [-0.4243, -0.3859],
        [-0.5, -0.2258],
        [-0.5, 0],
    ];

    const customVesselCategory: VesselCategoryType | undefined = vesselCategories.find((x) => {
        return x.port_id === portId && x.name === vesselCategory.name;
    });
    if (customVesselCategory !== undefined) {
        vesselShape = customVesselCategory.vessel_shape;
    } else if (Array.isArray(vesselCategory.vessel_shape)) {
        vesselShape = vesselCategory.vessel_shape;
    }

    const vesselSourceCenter = center
        ? sourcPosition
        : {
              east_utm: sourcPosition.east_utm + LOA / 2 - vesselOffset.stern_m,
              north_utm: sourcPosition.north_utm + BEAM / 2 - vesselOffset.stbd_m,
              heading_deg: sourcPosition.heading_deg,
          };

    const coordinates: number[][] = [];
    vesselShape.forEach((point) => {
        const x = point[0] * LOA + vesselSourceCenter.east_utm;
        const y = point[1] * BEAM + vesselSourceCenter.north_utm;
        coordinates.push([x, y]);
    });

    return {
        status: false,
        color: customVesselCategory ? customVesselCategory.color : vesselCategory.color,
        //@ts-ignore
        outline: rotateUTMPolygonToHeading(coordinates, vesselSourceCenter.heading_deg - 90, [
            sourcPosition.east_utm,
            sourcPosition.north_utm,
        ]),
    };
}

function createUnknownHeadingVessel(sourcPosition: PositionType, radius = 20, numPoints = 64) {
    const coordinates: number[][] = [];
    const angleStep = (2 * Math.PI) / numPoints;

    for (let i = 0; i < numPoints; i++) {
        const angle = i * angleStep;
        const x = sourcPosition.east_utm + radius * Math.cos(angle);
        const y = sourcPosition.north_utm + radius * Math.sin(angle);
        coordinates.push([x, y]);
    }

    coordinates.push(coordinates[0]);

    return coordinates;
}
function rotateUTMPolygonToHeading(coordinates, compassHeading, pivot) {
    // Adjust the angle to account for the compass-based heading
    const normalizedAngle = (360 - compassHeading) % 360; // Compass heading to mathematical rotation angle
    const angleRad = (Math.PI / 180) * normalizedAngle; // Convert angle to radians
    const cosAngle = Math.cos(angleRad);
    const sinAngle = Math.sin(angleRad);

    // Rotate each point in the polygon
    const rotatedCoordinates = coordinates.map(([x, y]) => {
        // Translate point to the origin (pivot)
        const translatedX = x - pivot[0];
        const translatedY = y - pivot[1];

        // Apply rotation matrix
        const rotatedX = translatedX * cosAngle - translatedY * sinAngle;
        const rotatedY = translatedX * sinAngle + translatedY * cosAngle;

        // Translate back to the original position
        return [rotatedX + pivot[0], rotatedY + pivot[1]];
    });

    return rotatedCoordinates;
}

export function findNearestPoint(
    points,
    target: { north_utm: number; east_utm: number },
    afterTime?: DateTimeFormat | null,
    beforeTime?: DateTimeFormat | null
): PositionType | null {
    let nearestPoint = null;
    let smallestDistance = Infinity;

    for (const point of points) {
        // Time filtering logic
        if (afterTime || beforeTime) {
            const pointTime = point.valid_time_utc.toTimestamp();
            const isAfterValid = afterTime ? pointTime >= afterTime.toTimestamp() : true;
            const isBeforeValid = beforeTime ? pointTime <= beforeTime.toTimestamp() : true;

            if (!isAfterValid || !isBeforeValid) {
                continue;
            }
        }
        // Calculate Euclidean distance
        const distance = Math.sqrt(
            Math.pow(point.north_utm - target.north_utm, 2) + Math.pow(point.east_utm - target.east_utm, 2)
        );

        if (distance < smallestDistance) {
            smallestDistance = distance;
            nearestPoint = point;
        }
    }

    return nearestPoint;
}

export function findNearestPointInTime(
    points: PositionType[],
    afterTime?: DateTimeFormat | null,
    beforeTime?: DateTimeFormat | null
): PositionType | null {
    if (!points || points.length === 0) return null;

    let nearestPoint: PositionType | null = null;

    for (const point of points) {
        const pointTime = point.valid_time_utc?.date;

        const isAfterValid = afterTime ? afterTime.isBefore(pointTime) || afterTime.isSame(pointTime) : true;
        const isBeforeValid = beforeTime ? beforeTime.isAfter(pointTime) || beforeTime.isSame(pointTime) : true;
        if (isAfterValid && isBeforeValid) {
            if (!nearestPoint || nearestPoint.valid_time_utc.isBefore(pointTime)) {
                nearestPoint = point;
            }
        }
    }

    return nearestPoint;
}
